<template>
  <div>
    <Head title="Panel de Administración" />

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-gray-900">Panel de Administración</h1>
        <p class="text-sm text-gray-500">Visión general del estado del sistema y usuarios.</p>
      </div>

      <!-- KPI Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <!-- Total Users -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div class="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <i class="fas fa-users text-lg" />
            </div>
            <h3 class="text-3xl font-bold text-slate-800">{{ totalUsers }}</h3>
          </div>
          <p class="text-sm font-medium text-slate-500 mt-2">Usuarios Registrados</p>
        </div>

        <!-- Monthly Revenue -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div class="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center mb-4">
              <i class="fas fa-sack-dollar text-lg" />
            </div>
            <h3 class="text-3xl font-bold text-slate-800">${{ monthlyRevenue?.toFixed(2) }}</h3>
          </div>
          <p class="text-sm font-medium text-slate-500 mt-2">Ingresos Mensuales Est.</p>
        </div>

        <!-- Total Accounts -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div class="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center mb-4">
              <i class="fas fa-building text-lg" />
            </div>
            <h3 class="text-3xl font-bold text-slate-800">{{ totalAccounts }}</h3>
          </div>
          <div class="flex items-center justify-between mt-2">
            <p class="text-sm font-medium text-slate-500">Cuentas Totales</p>
            <span class="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full font-bold">
              {{ paidAccounts }} Pagas
            </span>
          </div>
        </div>

        <!-- Active Subscriptions Estimate (Placeholder/Logic based) -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div class="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center mb-4">
              <i class="fas fa-chart-pie text-lg" />
            </div>
            <h3 class="text-3xl font-bold text-slate-800">{{ calculateConversionRate() }}%</h3>
          </div>
          <p class="text-sm font-medium text-slate-500 mt-2">Tasa de Conversión (Pagos)</p>
        </div>
      </div>

      <!-- Quick Actions / Navigation -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link href="/admin/external-sales" class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center hover:shadow-md transition-shadow hover:bg-gray-50">
          <div class="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center mr-4">
            <i class="fas fa-exchange-alt text-lg" />
          </div>
          <div>
            <h3 class="text-lg font-bold text-slate-800">Ventas Externas</h3>
            <p class="text-sm text-slate-500">Ver movimientos</p>
          </div>
        </Link>
      </div>

      <!-- Subscription Requests -->
      <div v-if="subscriptionRequests && subscriptionRequests.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-orange-50">
          <h2 class="text-lg font-bold text-orange-800">Solicitudes de Suscripción Pendientes</h2>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="bg-white text-gray-500 font-medium border-b border-gray-100">
              <tr>
                <th class="py-3 px-6">Usuario</th>
                <th class="py-3 px-6">Plan Solicitado</th>
                <th class="py-3 px-6">Monto</th>
                <th class="py-3 px-6">Banco / Origen</th>
                <th class="py-3 px-6">Referencia Pago</th>
                <th class="py-3 px-6">Fecha</th>
                <th class="py-3 px-6 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="request in subscriptionRequests" :key="request.id" class="hover:bg-gray-50/50 transition-colors">
                <td class="py-3 px-6 font-medium text-gray-900">
                  {{ request.user?.first_name }} {{ request.user?.last_name }}
                  <div class="text-xs text-gray-400">{{ request.user?.email }}</div>
                </td>
                <td class="py-3 px-6">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 uppercase">
                    {{ request.plan }}
                  </span>
                </td>
                <td class="py-3 px-6 font-bold text-green-700">
                  ${{ request.amount }}
                </td>
                <td class="py-3 px-6 text-gray-600">
                  {{ request.bank_name }}
                </td>
                <td class="py-3 px-6 font-mono text-gray-600 font-bold">
                  {{ request.payment_reference }}
                </td>
                <td class="py-3 px-6 text-gray-500">
                  {{ formatDate(request.created_at) }}
                </td>
                <td class="py-3 px-6 text-right">
                  <button :disabled="processingId === request.id" class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50" @click="approveRequest(request)">
                    <span v-if="processingId === request.id">Procesando...</span>
                    <span v-else>Aprobar</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Recent Users Table -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 class="text-lg font-bold text-gray-900">Usuarios Recientes</h2>
          <Link href="/admin/accounts" class="text-sm text-orange-600 font-medium hover:text-orange-700"> Ver todos </Link>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
              <tr>
                <th class="py-3 px-6">Usuario</th>
                <th class="py-3 px-6">Email</th>
                <th class="py-3 px-6">Plan</th>
                <th class="py-3 px-6 text-right">Registro</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="user in recentUsers" :key="user.id" class="hover:bg-gray-50/50 transition-colors">
                <td class="py-3 px-6 font-medium text-gray-900">
                  {{ user.first_name }} {{ user.last_name }}
                </td>
                <td class="py-3 px-6 text-gray-500">{{ user.email }}</td>
                <td class="py-3 px-6">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                    :class="getPlanColor(user.account?.plan)"
                  >
                    {{ getPlanName(user.account?.plan) }}
                  </span>
                </td>
                <td class="py-3 px-6 text-right text-gray-500">
                  {{ formatDate(user.created_at) }}
                </td>
              </tr>
              <tr v-if="!recentUsers?.length">
                <td colspan="4" class="py-8 text-center text-gray-400">
                  No hay usuarios recientes.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    totalUsers: Number,
    totalAccounts: Number,
    paidAccounts: Number,
    monthlyRevenue: Number,
    recentUsers: Array,
    subscriptionRequests: Array,
  },

  data() {
    return {
      processingId: null,
    }
  },

  methods: {
    approveRequest(request) {
      if (!confirm(`¿Estás seguro de aprobar la suscripción para ${request.user.first_name}?`)) return

      this.processingId = request.id
      Inertia.post(`/admin/subscription-requests/${request.id}/approve`, {}, {
        onFinish: () => {
          this.processingId = null
        },
      })
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-VE', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
      })
    },

    getPlanName(plan) {
      const names = {
        'free': 'Gratis',
        'entrepreneur': 'Emprendedor',
        'sme': 'Pymes',
        'enterprise': 'Empresas',
      }
      return names[plan] || 'Desconocido'
    },

    getPlanColor(plan) {
      const colors = {
        'free': 'bg-gray-100 text-gray-600',
        'entrepreneur': 'bg-blue-50 text-blue-700',
        'sme': 'bg-purple-50 text-purple-700',
        'enterprise': 'bg-orange-50 text-orange-700',
      }
      return colors[plan] || 'bg-gray-100 text-gray-600'
    },

    calculateConversionRate() {
      if(this.totalAccounts === 0) return 0
      return ((this.paidAccounts / this.totalAccounts) * 100).toFixed(1)
    },
  },
}
</script>